import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, MailWarning } from "lucide-react";
import { authApi } from "@/api/services";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { AuthShell } from "@/components/auth/AuthShell";

type Phase = "verifying" | "verified" | "error" | "no-token";

/**
 * Consumes the signup email-verification token (`/verify-email?token=...`),
 * auto-confirming on mount. On an expired/invalid link (or no token) it offers
 * to resend. Verification is SOFT — login is never blocked on it — so this page
 * is a finish-setup step, not a gate.
 */
function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [phase, setPhase] = useState<Phase>(token ? "verifying" : "no-token");
  const [error, setError] = useState<string | null>(null);
  const [resendEmail, setResendEmail] = useState("");
  const [resent, setResent] = useState(false);

  useEffect(() => {
    if (!token) return;
    let active = true;
    authApi
      .confirmEmailVerification(token)
      .then(() => active && setPhase("verified"))
      .catch((err: unknown) => {
        if (!active) return;
        const detail = (err as { response?: { data?: { detail?: string } } })
          ?.response?.data?.detail;
        setError(detail || "This verification link is invalid or has expired.");
        setPhase("error");
      });
    return () => {
      active = false;
    };
  }, [token]);

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    // Same outcome regardless (no email enumeration).
    try {
      await authApi.resendEmailVerification(resendEmail);
    } catch {
      /* ignore */
    }
    setResent(true);
  };

  const backToSignIn = (
    <Link
      to="/login"
      className="font-medium text-brand-600 transition-colors hover:text-brand-700"
    >
      Back to sign in
    </Link>
  );

  if (phase === "verifying") {
    return (
      <AuthShell
        eyebrow="Verify"
        title="Verifying your email…"
        subtitle="One moment while we confirm your address."
        illustrationTagline="Almost there."
        footer={backToSignIn}
      >
        <div className="flex justify-center py-6">
          <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
        </div>
      </AuthShell>
    );
  }

  if (phase === "verified") {
    return (
      <AuthShell
        eyebrow="Done"
        title="Email verified"
        subtitle="Your email address is confirmed."
        illustrationTagline="You're all set."
        footer={backToSignIn}
      >
        <div className="flex flex-col items-center gap-4 py-4 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600 shadow-[var(--shadow-glow)] dark:bg-brand-900/60 dark:text-brand-300">
            <CheckCircle2 className="h-7 w-7" />
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Thanks! You can head back to the app.
          </p>
        </div>
      </AuthShell>
    );
  }

  // error or no-token → offer a resend
  return (
    <AuthShell
      eyebrow="Verify"
      title={phase === "no-token" ? "Verify your email" : "Link expired"}
      subtitle={
        phase === "no-token"
          ? "Enter your email and we'll send a fresh verification link."
          : error || "This verification link is invalid or has expired."
      }
      illustrationTagline="We'll send a new link."
      footer={backToSignIn}
    >
      {resent ? (
        <div className="flex flex-col items-center gap-4 py-4 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600 dark:bg-brand-900/60 dark:text-brand-300">
            <MailWarning className="h-7 w-7" />
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            If your account needs verification, a new link is on its way.
          </p>
        </div>
      ) : (
        <form onSubmit={handleResend} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="resend-email">Email</Label>
            <Input
              id="resend-email"
              type="email"
              autoComplete="email"
              value={resendEmail}
              onChange={(e) => setResendEmail(e.target.value)}
              required
              placeholder="you@company.com"
            />
          </div>
          <Button type="submit" className="w-full">
            Send a new link
          </Button>
        </form>
      )}
    </AuthShell>
  );
}

export default VerifyEmail;
